import {FilterValue, SorterResult, TablePaginationConfig} from "antd/lib/table/interface";
import {ListQuotesResponse, SalesTeamRequest} from "../api";
import {RowSelectMethod} from "antd/es/table/interface";
import BMButton, {BMButtonProps} from "./BMButton";
import {useIntl} from "react-intl";
import {Key, useContext, useState} from "react";
import {ConfiguratorContext} from "../context";
import {useForm} from "antd/es/form/Form";
import {BaseQuote, QuoteFilter} from "../api/models";
import SalesTeamForm, {SalesTeamFormValues} from "./Quote/SalesTeamForm";
import useQuoteList from "../swr/useQuoteList";
import {Button, Descriptions, notification, Result, Table} from "antd";
import ModalWizard from "./ModalWizard";
import QuoteFilterControls from "./QuoteFilterControls";
import {BaseQuoteDescription} from "../pages/quotes";
import Utils from "../util/util";
import {SalesTeamDescription} from "./SelectSalesTeamButton";
import {DescriptionsItemType} from "antd/es/descriptions";

export type ListQuotesResponseSort = SorterResult<ListQuotesResponse> | SorterResult<ListQuotesResponse>[]
interface RowSelection<T> {
    selections: T[],
    type:RowSelectMethod
}
export type QuoteSelection = RowSelection<number>;
const AssignQuoteButtonModal = (props: Omit<BMButtonProps, "id" | "value" | "onChange" > & {
    onChange?:() => void
    onCancel?:() => void
}) => {

    const {onChange:b, ...btnProps} = props;

    const intl = useIntl();
    const configurator = useContext(ConfiguratorContext);

    const [form] = useForm();
    const [isOpen, setIsOpen] = useState<boolean>();
    const [isLoading, setIsLoading] = useState<boolean>();
    const [filter, setFilter] = useState<QuoteFilter | undefined>({
        stock: true,
    });
    const [selectType, setselectType] = useState<RowSelectMethod>();
    const [selected, setSelected] = useState<Record<number, QuoteSelection>>();
    const [selectedSalesTeam, setSelectedSalesTeam] = useState<SalesTeamFormValues>();
    const [sort, setSort] = useState<ListQuotesResponseSort>({
        columnKey: 'updatedAt',
        order: 'descend'
    });
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        pageSize: 5,
        current: 1,
        showLessItems: true,
    });

    const quoteLst = useQuoteList({
        filter,
        current: pagination.current,
        pageSize: pagination.pageSize,
        sorter: sort
    });

    const handleTableChange = (pagination: TablePaginationConfig, _filters: Record<string, FilterValue | null>, sorter: ListQuotesResponseSort) => {
        setPagination(pagination);

        setSort(sorter);
    };

    const handleChangeFilter = (f) => {
        setFilter(f);
        setPagination({
            ...pagination,
            current: 1,
        })
    }


    const handleCancel = () => {
        setIsOpen(false);
        props.onCancel?.();
    }

    const handleSelectRow = (selectedRowKeys: Key[], _selectedRows:BaseQuote[], info: { type:RowSelectMethod } ) => {

        const lst = selectedRowKeys as number[];

        setselectType(
            info.type == "single" ? info.type
                :info.type != selectType ? info.type
                    :"single" );

        setSelected( {
            ...selected,
            [pagination.current || 0]: {selections: lst, type:info.type}
        });
    }

    const handleSelectedQuotes = (nav) => {
        nav.nextStep();
    }
    const handleUpdateTeam = async (nav) => {

        const values = await form.validateFields();
        setSelectedSalesTeam(values);
        nav.nextStep();
    }
    const handleSave = async (nav) => {

        if ( !!selectedSalesTeam ) {
            const quotes = selectType == "all" ? undefined : Object.values(selected || {}).map(s => s.selections).flat();
            const salesTeam = {
                id: selectedSalesTeam.id,
                name: selectedSalesTeam?.name,
                engineers: selectedSalesTeam?.engineers?.map(u => u.id),
                sales: selectedSalesTeam?.sales?.map(u => u.id),
                support: selectedSalesTeam?.support?.map(u => u.id),
                viewers: selectedSalesTeam?.viewers?.map(u => u.id),
            }
            await updateQuotes( salesTeam, quotes, filter );
        }
        setIsOpen(false);
    }

    const updateQuotes = async ( salesTeam:SalesTeamRequest, selected: number[] | undefined, filter: QuoteFilter | undefined ) : Promise<void> => {

        setIsLoading(true);
        try {
            await configurator.api.patchQuoteSalesTeamList( salesTeam, {
                selected, filter
            } );
        } catch (e: any) {
            const errorMsg = intl.formatMessage({ id: e.message });
            notification.error( { message: "Error updating quotes.  " + errorMsg });
        }
        setIsLoading(false);

        return;
    };


    return <>
        <BMButton {...btnProps}
                  onClick={() => setIsOpen(true)}
        />
        <ModalWizard
            open={isOpen}
            width={"50rem"}
            onCancel={handleCancel}
            showSteps={false}
            afterOpenChange={(open) => {
                if(open) {
                    setselectType(undefined);
                    setSelected(undefined);
                    setFilter(undefined);
                    form.resetFields();
                    quoteLst.mutate();
                }}}
            steps={[
                {
                    key:"selectQuote",
                    title: "Select Quotes",
                    body:(_nav) => <div>
                        <QuoteFilterControls filter={filter}
                                             onFilterChange={handleChangeFilter} />
                        <Table
                            rowKey="id"
                            dataSource={quoteLst.data?.content}
                            loading={quoteLst.isLoading}
                            onChange={handleTableChange}
                            rowSelection={{
                                type:  "checkbox",
                                onChange: handleSelectRow,
                                hideSelectAll: false,
                                selectedRowKeys: (selectType == "all")
                                    ? quoteLst.data?.content.map(q => q.id )
                                    : selected?.[pagination.current || 0]?.selections,
                            }}
                            pagination={{...pagination, total: quoteLst.data?.totalElements}}
                            columns={[
                                {
                                    title: "Quote Details",
                                    key: "quoteId",
                                    render: (q) => <BaseQuoteDescription quote={q} target={"_blank"}/>,
                                    sorter: true,
                                },
                                {
                                    title: "Sales Person",
                                    key: "owner",
                                    render: (_owner, q) =>
                                        <div style={{minWidth: "9rem"}}>
                                            {Utils.formatUsername(q.owner)}
                                        </div>,
                                    sorter: true,
                                },
                                {
                                    title: "Sales Team",
                                    key: "salesTeam",
                                    render: (_owner, q) =>
                                        <div style={{minWidth: "9rem"}}>
                                            <SalesTeamDescription salesTeam={q.salesTeam} readOnly={true} />
                                        </div>,
                                    sorter: true,
                                }
                            ]}
                        />
                    </div>,
                    footer:(nav) => <div style={{display: "flex", justifyContent: "space-between", padding: "1rem .3rem .3rem .3rem" }}>
                        <Button key="cancel" onClick={handleCancel}>Cancel</Button>
                        <Button key="next" type="primary" loading={isLoading} onClick={() => handleSelectedQuotes(nav)} >Next</Button>
                    </div>
                },
                {
                    key:"updateTeam",
                    title: "Update Team",
                    body:(_nav) =>
                        <div>
                            <div style={{fontStyle: "italic", marginBottom: ".5rem"}}>*Only selected fields will be updated.</div>
                            <SalesTeamForm form={form} />
                        </div>,
                    footer:(nav) => <div style={{display: "flex", justifyContent: "space-between", padding: "1rem .3rem .3rem .3rem" }}>
                        <Button key="back" onClick={() => nav.prevStep()}>Back</Button>
                        <Button key="next" type="primary" loading={isLoading} onClick={() => handleUpdateTeam(nav)} >Next</Button>
                    </div>
                },
                {
                    key:"confirm",
                    body:(_nav) => {

                        const items:DescriptionsItemType[] = [];
                        items.push( {
                            label: "Quotes",
                            children: selectType == "all" ? quoteLst.data?.totalElements : Object.values(selected || {}).map(s => s.selections).flat().length
                        });
                        !!selectedSalesTeam?.name?.length && items.push( {
                            label: "Name",
                            children: selectedSalesTeam?.name
                        });
                        !!selectedSalesTeam?.sales?.length && items.push( {
                            label: "Sales",
                            children: selectedSalesTeam?.sales?.map(u => u.name ).join(", ")
                        });
                        !!selectedSalesTeam?.engineers?.length && items.push( {
                            label: "Engineering",
                            children: selectedSalesTeam?.engineers?.map(u => u.name ).join(", ")
                        });
                        !!selectedSalesTeam?.support?.length && items.push( {
                            label: "Support",
                            children: selectedSalesTeam?.support?.map(u => u.name ).join(", ")
                        });
                        !!selectedSalesTeam?.viewers?.length && items.push( {
                            label: "Viewers",
                            children: selectedSalesTeam?.viewers?.map(u => u.name ).join(", ")
                        });

                        return <div>
                            <Result
                                title="Please confirm the following updates:"
                                style={{padding: "0"}}
                            >
                                <Descriptions
                                    column={1}
                                    className="dialog-salesTeam"
                                    size="small"
                                    items={items}
                                />
                            </Result>
                        </div>
                    },
                    footer:(nav) => <div style={{display: "flex", justifyContent: "space-between", padding: "1rem .3rem .3rem .3rem" }}>
                        <Button key="back" onClick={() => nav.prevStep()}>Back</Button>
                        <Button key="next" type="primary" loading={isLoading} onClick={() => handleSave(nav)} >Save</Button>
                    </div>
                },
            ] }
        />
    </>;
}

export default AssignQuoteButtonModal;
